import React, { useState, useEffect } from "react";
import { Select, Button, Modal, Empty } from "antd";
import { RightOutlined, FilterFilled, FilterOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import "./MobileTable.css";
import { devLogging } from "src/utils/logger";
import { getPodIdAndPodNameMapping } from "src/utils/getFilterOptions";
import { safeGet, truncate } from "src/shared/utils";
import { IM_COM_TABS } from "src/shared/config";

const { Option } = Select;

const MobileTable = ({ timeTab, metaData, data, allowGoingNextPage = true, userRolesPayload }) => {
  const [filterStoreIds, setFilterStoreIds] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedPod, setSelectedPod] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const isPositive = metaData?.isPositive || null;
  const benchMark = metaData?.benchMark || null;

  const history = useHistory();

  const handleArrowClick = (storeId) => {
    history.push(`/im-com-app/third-screen?store_id=${storeId}`);
  };

  devLogging("table data: ", data);

  // If no data is available, show a message
  if (!data?.payload?.records?.length) {
    return (
      <div
        className="pod-section"
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "2rem",
          margin: "1rem",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Empty
          description={<span style={{ fontSize: "16px", color: "#595959" }}>No data available</span>}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  // Extract unique city and pod options from the table data
  const cityOptions = [...new Set(data.payload.records.map((record) => record.city))].map((city) => ({
    value: city,
    label: city,
  }));

  const podMapping = getPodIdAndPodNameMapping(userRolesPayload);

  const podOptions = selectedCity
    ? [
        ...new Set(
          data.payload.records.filter((record) => record.city === selectedCity).map((record) => record.store_id)
        ),
      ].map((pod) => ({
        value: pod,
        label: pod + " - " + podMapping[pod] || pod, // Use the mapping to get the pod name, fallback to pod ID if not found
      }))
    : [];

  // Filter records by store_id and city if filters are applied
  const filteredRecords = data.payload.records.filter((record) => {
    const matchesCity = selectedCity ? record.city === selectedCity : true;
    const matchesPod = filterStoreIds.length ? filterStoreIds.includes(record.store_id.toString()) : true;
    return matchesCity && matchesPod;
  });

  const handleApplyFilters = () => {
    setIsModalVisible(false); // Hide the modal on apply
  };

  const isFilterActive = selectedPod.length || selectedCity;

  return (
    <div className="mobile-table">
      {/* Filter Icon */}
      <div style={{ marginLeft: "1rem", color: "black" }}>
        {isFilterActive ? (
          <FilterFilled style={{ fontSize: "2rem", color: "blue" }} onClick={() => setIsModalVisible(true)} />
        ) : (
          <FilterOutlined style={{ fontSize: "2rem" }} onClick={() => setIsModalVisible(true)} />
        )}
      </div>

      {/* Filter Modal */}
      <Modal
        title="Filter Options"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        maskStyle={{
          backgroundColor: "rgba(0, 0, 0, 0.9)",
        }}
        footer={[
          <Button
            key="clear"
            onClick={() => {
              setSelectedPod([]);
              setSelectedCity("");
              setFilterStoreIds([]);
              //close the modal
              setIsModalVisible(false);
            }}
          >
            Clear Filters
          </Button>,
          <Button key="ok" type="primary" onClick={handleApplyFilters}>
            OK
          </Button>,
        ]}
      >
        <Select
          id="cityFilter"
          value={selectedCity}
          onChange={(value) => {
            setSelectedCity(value);
            setSelectedPod([]); // Reset selected pod when city changes
          }}
          placeholder="Filter by City"
          style={{ width: "100%", marginBottom: "16px" }}
        >
          {cityOptions.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>

        <Select
          id="podFilter"
          mode="multiple"
          value={selectedPod}
          onChange={(value) => {
            setFilterStoreIds(value);
            setSelectedPod(value);
          }}
          placeholder="Select a Pod"
          showSearch
          allowClear
          filterOption={(input, option) => {
            const label = option?.label;
            return typeof label === "string" && label.toLowerCase().includes(input.toLowerCase());
          }}
          options={podOptions}
          style={{ width: "100%", maxHeight: "100px" }}
          dropdownStyle={{ maxHeight: "200px", overflow: "auto" }}
          disabled={!selectedCity} // Disable if no city is selected
        />
      </Modal>

      {/* Store ID Dropdown Filter */}
      <div className="filter-section sticky" style={{ marginBottom: "10px" }}>
        <Select
          mode="multiple"
          allowClear
          style={{ width: "100%", maxHeight: "200px", overflowY: "auto", display: "none" }}
          placeholder="Select store IDs"
          value={filterStoreIds}
          onChange={(value) => setFilterStoreIds(value)}
        >
          {filteredRecords.map((record) => (
            <Option key={record.store_id} value={record.store_id.toString()}>
              {record.store_id}
            </Option>
          ))}
        </Select>
      </div>

      {/* Display filtered records */}
      {filteredRecords.length === 0 ? (
        <p>No matching records found</p>
      ) : (
        filteredRecords.map((record, index) => (
          <div className="pod-section" key={index}>
            {/* Store Header */}
            <div className="pod-header">
              <div>
                <p className="pod-name">{record.store_id} </p>
                <p style={{ fontSize: "1rem" }}> {truncate(safeGet(podMapping, record.store_id), 35)}</p>
                <p className="pod-location">{record.city}</p>
              </div>
              {allowGoingNextPage && (
                <div
                  className="arrow"
                  onClick={() => handleArrowClick(record.store_id)}
                  style={{
                    cursor: "pointer",
                    backgroundColor: "#f0f0f0",
                    padding: "0.7rem",
                    borderRadius: "5px",
                    fontWeight: "bold",
                  }}
                >
                  <RightOutlined />
                </div>
              )}
            </div>

            {/* Store Details */}
            <div className="details">
              {Object.entries(record).map(([key, value]) => {
                if (key === "metric_breakdown") {
                  console.log("metric breakdown ", value);
                  try {
                    const parsedMetricBreakdown = JSON.parse(value);
                    return (
                      <div key={key}>
                        <MetricTable
                          timeTab={timeTab}
                          benchMark={benchMark}
                          isPositive={isPositive}
                          data={parsedMetricBreakdown}
                        />
                      </div>
                    );
                  } catch (error) {
                    console.error("Error parsing metric_breakdown:", error);
                    return (
                      <p key={key}>
                        <strong>{key.replace(/_/g, " ")}:</strong> Invalid JSON
                      </p>
                    );
                  }
                }
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const MetricTable = ({ timeTab, benchMark, isPositive, data }) => {
  const dayGranularity = IM_COM_TABS.dayGranularityTabsArray.includes(timeTab);

  if (!data || Object.keys(data).length === 0) {
    return <p>No data available</p>;
  }

  const [mainMetric, ...restKeys] = Object.keys(data);
  const mainMetricData = data[mainMetric];
  const breakdownData = mainMetricData.breakdown;

  return (
    <div style={{ overflowX: "auto", borderRadius: "5px" }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead style={{ backgroundColor: "lightgrey" }}>
          <tr>
            <th style={cellStyle}>Key</th>
            {!dayGranularity && (
              <>
                <th style={cellStyle}>Curr. Hour</th>
                <th style={cellStyle}>Last Hour</th>
              </>
            )}
            <th style={cellStyle}>Today</th>
            <th style={cellStyle}>Yesterday</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={cellStyle}>{mainMetric.toUpperCase()}</td>
            {!dayGranularity && (
              <>
                <td style={getCellStyleBasedOnBenchmark(mainMetricData.cur_hour, benchMark, isPositive)}>
                  {mainMetricData.cur_hour}
                </td>
                <td style={getCellStyleBasedOnBenchmark(mainMetricData.last_hour, benchMark, isPositive)}>
                  {mainMetricData.last_hour}
                </td>
              </>
            )}
            <td style={getCellStyleBasedOnBenchmark(mainMetricData.cur_day, benchMark, isPositive)}>
              {mainMetricData.cur_day}
            </td>
            <td style={getCellStyleBasedOnBenchmark(mainMetricData.last_day, benchMark, isPositive)}>
              {mainMetricData.last_day}
            </td>
          </tr>
          {breakdownData &&
            Object.entries(breakdownData).map(([key, value]) => (
              <tr key={key}>
                <td style={cellStyle}>{key.replace(/_/g, " ").toUpperCase()}</td>
                {!dayGranularity && (
                  <>
                    <td style={cellStyle}>{value.cur_hour}</td>
                    <td style={cellStyle}>{value.last_hour}</td>
                  </>
                )}
                <td style={cellStyle}>{value.cur_day}</td>
                <td style={cellStyle}>{value.last_day}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};

const cellStyle = {
  border: "1px solid #ddd",
  padding: "8px",
  textAlign: "center",
};

const getCellStyleBasedOnBenchmark = (value, benchMark, isPositive) => {
  const baseStyle = {
    border: "1px solid #ddd",
    padding: "8px",
    textAlign: "center",
  };

  const violatedBenchMarkStyle = {
    ...baseStyle,
    backgroundColor: "#EF4D51",
    color: "white",
    fontWeight: "bold",
  };

  if (value === null || value === undefined || benchMark === null || benchMark === undefined) {
    return baseStyle;
  }

  const numValue = parseFloat(value);
  const numBenchmark = parseFloat(benchMark);

  if ((isPositive && numValue < numBenchmark) || (!isPositive && numValue > numBenchmark)) {
    return violatedBenchMarkStyle;
  }

  return baseStyle;
};

export default MobileTable;
